<template>
  <GChart
    class="chart"
    :class="selectUnit"
    ref="chart"
    :type="type"
    :data="data"
    :options="chartOptions"
    :resizeDebounce="10"
    :style="selectedRowYear"
    @ready="onChartReady"
  />
</template>

<script>
import { merge, cloneDeep, flatten } from 'lodash'
import { mapState, mapGetters } from 'vuex'
import { GChart } from 'vue-google-charts'

export { default as FrameTemplate } from './FrameTemplate'
export { default as YearLabels } from './YearLabels'

const baseOptions = {
  domainAxis: { type: 'category' },
  height: 335,
  legend: 'none',
  pointSize: 3,
  lineWidth: 1.5,
  tooltip: { isHtml: true, trigger: 'selection' },
  vAxis: {
    gridlines: { color: '#F2F2F2' },
    minorGridlines: { count: 0 },
    textStyle: {
      fontSize: 11,
      color: '#5A5A5A',
    },
    format: 'decimal',
    baselineColor: '#CCCCCC',
    maxValue: 5,
    viewWindow: { min: 0 },
  },
  hAxis: {
    gridlines: { color: 'transparent' },
    textStyle: {
      fontSize: 11,
      color: '#5A5A5A',
    },
    showTextEvery: 1,
    minTextSpacing: 0,
    viewWindow: { min: 0 },
  },
  chartArea: {
    width: '100%',
    left: 49,
    top: 24,
    height: 294,
  },
}

/**
 * propsで渡されたoptionを強制的に上書きする
 */
const forceOverrideOptions = (appendOption, isZeroOnly, type) => {
  const viewWindowOption =
    // 横向きの場合
    type === 'BarChart'
      ? {
          hAxis: {
            viewWindow: {
              max: isZeroOnly ? 1000 : undefined,
            },
          },
        }
      : {
          vAxis: {
            viewWindow: {
              max: isZeroOnly ? 1000 : undefined,
            },
          },
        }
  return merge(cloneDeep(appendOption), viewWindowOption)
}

/**
 * チャートコンポーネント
 *
 * [vue-google-charts](https://github.com/devstark-com/vue-google-charts) を使用しています
 * [google chart](https://developers.google.com/chart/interactive/docs/gallery) の一覧はこちら
 */

export default {
  name: 'Chart',
  components: {
    GChart,
  },
  props: {
    type: { type: String, default: 'ColumnChart' },
    data: { type: Array, default: () => [] },
    title: { type: String, default: '' },
    isStacked: { type: Boolean, default: false },
    options: { type: Object, default: () => ({}) },
    unit: { type: Number, default: 0 },
    yearMonths: { type: Array, default: () => [] },
  },
  data() {
    return {
      chart: null,
      selectedYearValue: null,
    }
  },
  computed: {
    ...mapState({
      menuOpen: state => state.analysis.menuOpen,
    }),
    ...mapGetters({
      isMonthly: 'analysis/unit/isMonthly',
    }),
    chartOptions() {
      return {
        title: this.title,
        isStacked: this.isStacked,
        ...merge(cloneDeep(baseOptions), forceOverrideOptions(this.options, this.isZeroOnly, this.type)),
      }
    },
    isZeroOnly() {
      if (!this.data || this.data.length === 0) return false
      const [columns, ...rows] = this.data
      const dataIndexes = columns
        .slice(1) // legend列を取り除く
        .map((column, index) => (typeof column === 'object' ? false : index)) // annotationの列を取り除く
        .filter(index => typeof index === 'number')
      // 値のみを抜き出しすべて0かどうかをチェックする
      return flatten(dataIndexes.map(dataIndex => rows.map(row => row.slice(1)[dataIndex]))).every(value => value === 0)
    },
    selectUnit() {
      switch (this.unit) {
        case 1:
          return 'isHourly'
        case 2:
          return 'isDaily'
        case 3:
          return 'isWeekly'
        case 4:
          return 'isMonthly'
        default:
          return ''
      }
    },
    selectedRowYear() {
      if (this.selectedYearValue == null) return false
      return {
        '--year': `'${this.yearMonths[this.selectedYearValue].slice(0, 7)}'`,
      }
    },
  },
  watch: {
    menuOpen() {
      if (this.chart) {
        setTimeout(() => {
          this.redraw()
        }, 300)
      }
    },
  },
  mounted() {
    if (this.chart) window.addEventListener('resize', this.redraw)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.redraw)
  },
  methods: {
    onChartReady(chart) {
      this.chart = chart
      if (this.isMonthly(this.unit)) google.visualization.events.addListener(this.chart, 'select', this.selectedChart)
    },
    redraw() {
      this.chart.draw(google.visualization.arrayToDataTable(this.data), this.chartOptions)
    },
    selectedChart() {
      const selection = this.chart.getSelection()
      this.selectedYearValue = selection[0]?.row
    },
  },
}
</script>
<style lang="postcss" scoped>
.no-data {
  align-items: center;
  display: flex;
  font-size: 14px;
  height: 392px;
  justify-content: center;
  line-height: 1.7;
  text-align: center;
  white-space: break-spaces;
}

>>> .google-visualization-tooltip {
  height: 45px;

  .google-visualization-tooltip-item-list {
    line-height: 0.4;
    .isHourly & {
      > li:first-child span::after {
        content: ':00';
      }
    }
    .isDaily & {
      > li:first-child span::after {
        content: '日';
      }
    }
    .isWeekly & {
      > li:first-child span::after {
        content: '曜日';
      }
    }

    .isMonthly & {
      > li:first-child span {
        visibility: hidden;
      }

      > li:first-child span::before {
        content: var(--year);
        visibility: visible;
      }
    }
  }
}
</style>
